import { useCallback, useRef, useSyncExternalStore } from 'react'
import { useStoreApi } from '@xyflow/react'
import { panSector, type PanDirection } from '../utils/canvasPanDirection'

/**
 * Wohin gerade gefahren wird. `null`, solange der **Ausschnitt** ruht.
 *
 * Der Vektor kommt mit, weil das Ausweichen auf einen Nachbarn ihn braucht
 * (`highlightedHint`) — aber er wird nur mit dem Sektor zusammen erneuert,
 * nicht bei jedem Frame. Eine Geste soll sich nicht mitten im Ziehen für den
 * anderen Nachbarn entscheiden.
 */
export interface PanMotion {
  sector: PanDirection
  dx: number
  dy: number
}

/** Wie stark ein neuer Schritt das gleitende Mittel verschiebt. */
const SMOOTHING = 0.3

/**
 * Wie lange die Richtung nach der letzten Bewegung noch steht.
 *
 * Der Zeitgeber leistet zweierlei, und deshalb ist er großzügig bemessen:
 *
 * 1. **Er beendet die Fahrt.** `onMoveEnd` allein trüge das nicht: Beim
 *    Trackpad-Wischen wartet React Flow selbst 150ms ab, und das Mausrad
 *    meldet gar kein Ende. Der Zeitgeber ist das, was ALLE Arten zu pannen
 *    gemeinsam beenden.
 * 2. **Er lässt den Pfeil nachleuchten.** Mit 120ms erlosch er im selben
 *    Moment, in dem man losließ — die Hervorhebung war weg, bevor der Blick
 *    bei ihr ankam. Jetzt steht sie eine halbe Sekunde und blendet dann
 *    langsam ab (die Abblende selbst steckt im Stylesheet, weitere 0,4s).
 *
 * Ein angenehmer Nebeneffekt der größeren Zahl: Wer langsam zieht und
 * zwischendurch innehält, verliert die Hervorhebung nicht mehr.
 */
const IDLE_MS = 500

/**
 * Die Fahrtrichtung des **Ausschnitts** — für den **Randpfeil**, der in dieser
 * Richtung aufleuchtet (siehe „Randpfeil" in CONTEXT.md).
 *
 * **Gemeldet wird nur der SEKTOR, nicht die Bewegung.** React Flows Store
 * ändert sich beim Ziehen in jedem Frame; ein Wert, der daran hängt, zöge ein
 * Rendern je Frame nach sich — genau davor warnt der `getZoom()`-Kommentar in
 * `CanvasFlow`. Der Sektor wechselt ein- bis dreimal je Geste.
 *
 * Deshalb `useSyncExternalStore` und nicht `useStore`: Das Abonnement läuft
 * imperativ mit, der Schnappschuss bleibt derselbe, solange sich die Richtung
 * nicht ändert.
 *
 * **Geglättet und nicht roh:** Die Richtung entsteht aus einem gleitenden
 * Mittel über die letzten Schritte. Ein zittriges Frame darf den Pfeil nicht
 * umspringen lassen — genau das machte die Hervorhebung unruhig statt
 * hilfreich.
 *
 * **Zoomen zählt nicht.** Beim Zoomen um den Zeiger wandern `x` und `y` mit,
 * ohne dass jemand fährt; die Richtung wäre ein Rechenartefakt. Ändert sich
 * `zoom`, wird die laufende Fahrt gelöscht.
 *
 * @example
 * const motion = useCanvasPanDirection()
 * const lit = highlightedHint(motion?.sector ?? null, hints, motion?.dx ?? 0, motion?.dy ?? 0)
 */
export function useCanvasPanDirection(): PanMotion | null {
  const storeApi = useStoreApi()

  // Der Schnappschuss. MUSS zwischen den Aufrufen identisch bleiben, solange
  // sich die Richtung nicht ändert — `useSyncExternalStore` vergleicht per
  // Referenz und liefe sonst in eine Endlosschleife.
  const motionRef = useRef<PanMotion | null>(null)

  const subscribe = useCallback((onChange: () => void) => {
    let previous = storeApi.getState().transform
    // Das gleitende Mittel der letzten Schritte, in Bildschirm-Pixeln.
    let vx = 0
    let vy = 0
    let idle: ReturnType<typeof setTimeout> | undefined

    const clear = () => {
      vx = 0
      vy = 0
      if (motionRef.current !== null) {
        motionRef.current = null
        onChange()
      }
    }

    const unsubscribe = storeApi.subscribe(state => {
      const [x, y, zoom] = state.transform
      const [px, py, pz] = previous
      previous = state.transform

      if (zoom !== pz) {
        // Gezoomt, nicht gefahren.
        clearTimeout(idle)
        clear()
        return
      }
      if (x === px && y === py) return

      /*
        Die Bewegung des AUSSCHNITTS, nicht die der Hand — deshalb das
        Vorzeichen: React Flows `transform` verschiebt den INHALT. Wer die
        Fläche nach rechts zieht, lässt den Ausschnitt nach links wandern, und
        dort kommt Neues ins Bild. Genau dorthin zeigt der Randpfeil.
      */
      vx = vx * (1 - SMOOTHING) - (x - px) * SMOOTHING
      vy = vy * (1 - SMOOTHING) - (y - py) * SMOOTHING

      clearTimeout(idle)
      idle = setTimeout(clear, IDLE_MS)

      const sector = panSector(vx, vy)
      if (sector === null) return
      // Nur bei einem WECHSEL einen neuen Schnappschuss: Sonst rendert die
      // Anzeige in jedem Frame, und die Wahl zwischen zwei Nachbarn kippte
      // mitten in der Geste hin und her.
      if (motionRef.current?.sector === sector) return

      motionRef.current = { sector, dx: vx, dy: vy }
      onChange()
    })

    return () => {
      clearTimeout(idle)
      unsubscribe()
      motionRef.current = null
    }
  }, [storeApi])

  const getSnapshot = useCallback(() => motionRef.current, [])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
